import { uuidv4 } from "../utils/utils";
import { WorkerStatus } from "./WorkerProvider";

interface WorkerOptions {
  workerClass: any;
}

// Controller that handles all interaction with a given web worker
class WorkerController {
  private worker: Worker | null = null;
  private status: WorkerStatus = WorkerStatus.NOT_READY;
  private pendingMessages: { [key: string]: (response: any) => void } = {};

  constructor(private options: WorkerOptions) {}

  /**
   * Registers the worker and defines a listener to handle
   * messages coming from the worker.
   */
  registerWorker() {
    this.worker = new this.options.workerClass();

    this.worker.onmessage = (
      msg: MessageEvent<{
        messageId: string;
        payload: any;
      }>,
    ) => {
      const { messageId, payload } = msg.data;
      // Check if there's a pending message with the given messageId
      if (messageId in this.pendingMessages) {
        // Resolve the promise with the worker's response
        this.pendingMessages[messageId]({ messageId, payload });
        delete this.pendingMessages[messageId];
      }
    };
  }

  /**
   * Unregisters (terminates) the worker.
   */
  unregisterWorker() {
    this.worker?.terminate();
    this.status = WorkerStatus.NOT_READY;
    this.worker = null;
  }

  /**
   * Sends a message to the worker and returns a promise that
   * resolves when the worker responds.
   */
  postMessageToWorker<S = unknown, T = unknown>({
    type,
    payload,
    id,
  }: {
    type: string;
    payload?: T;
    id?: string;
  }): Promise<{
    messageId: string;
    payload: S;
  }> {
    if (!this.worker) {
      return Promise.reject("WorkerController is not initialized");
    }

    const messageId = id ?? uuidv4();

    // Post the message to the worker
    this.worker.postMessage({
      messageId,
      payload:
        payload !== null && payload !== undefined
          ? JSON.stringify(payload)
          : undefined,
      type,
    });

    // Return a promise that will be resolved when the worker responds
    return new Promise((resolve) => {
      // Store the resolver function, which will be called once the worker responds
      this.pendingMessages[messageId] = resolve;
    });
  }

  /**
   * Sends several requests to the worker as a batch.
   */
  postBatchedMessageToWorker<S = unknown, T = unknown>({
    type,
    payload,
  }: {
    type: string;
    payload: T[];
  }) {
    if (!this.worker) {
      return Promise.reject("WorkerController is not initialized");
    }

    return Promise.allSettled(
      payload.map((batch) => {
        return this.postMessageToWorker<S, T>({ payload: batch, type });
      }),
    );
  }

  /**
   * Deletes request from list of pending messages
   */

  resolveTaskWithId(id: string) {
    delete this.pendingMessages[id];
  }

  setWorkerStatus(status: WorkerStatus) {
    this.status = status;
  }

  getWorkerStatus(): WorkerStatus {
    return this.status;
  }
}

export default WorkerController;
